import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { DeleteButtonWithConfirmation } from 'components/Common/Button/DeleteButtonWithConfirmation'
import { MoneyInput } from 'components/Common/Input/MoneyInput'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { useAppController } from 'customHooks/useAppController'
import { bomItemQuantitySelector } from 'features/BillOfMaterials/store/selectors/bomItemQuantitySelector'
import { bomItemSelector } from 'features/BillOfMaterials/store/selectors/bomItemSelector'
import { projectIdSelector } from 'features/BillOfMaterials/store/selectors/projectIdSelector'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { RequestForQuotationController } from 'features/RequestForQuotations/RFQ/RequestForQuotationsController'
import { isEqual } from 'lodash'
import {
  AssemblyHeaderRow,
  BomItemType,
  PartTypeRow,
} from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Money,
  MoneyDto,
  Quotation,
  QuotationLineDto,
  RequestForQuotationDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import store, { RootAppState, useAppSelector } from 'store/configureStore'
import { useShowException } from 'utils/useShowException'
import { ProjectQuotationsController } from '../ProjectQuotationController'

const quotationResponseColorMap: Record<
  Quotation['state'],
  {
    color:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'error'
      | 'info'
      | 'success'
      | 'warning'
    variant: 'filled' | 'outlined'
  }
> = {
  New: { color: 'warning', variant: 'filled' },
  Cancelled: { color: 'error', variant: 'filled' },
  QuotationResponseAcceptedByBuyer: { color: 'success', variant: 'filled' },
  QuotationResponseCreatedBySupplier: { color: 'info', variant: 'filled' },
  QuotationResponseRejectedByBuyer: { color: 'error', variant: 'filled' },
  QuotationResponseMergedIntoProject: { color: 'default', variant: 'filled' },
  QuotationResponseMergeError: { color: 'default', variant: 'filled' },
  QuotationResponseReceivedByBuyer: { color: 'default', variant: 'filled' },
}

export type ManageQuotationResponseDialogProps = {
  isOpen: boolean
  onClose: () => void
}

type RfqBomItem = NonNullable<
  RequestForQuotationDto['bomItemsAndActivities']
>[number]

type LocalResponsePriceMap = Record<string, Money>

function formatMoney(money: MoneyDto | null | undefined): string {
  if (!money?.value && money?.value !== 0) {
    return '-'
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.value)
  } catch {
    return `${money.value} ${money.currency || ''}`.trim()
  }
}

function getBomItemType(
  state: RootAppState,
  bomItemId: string,
): BomItemType | null {
  if (!bomItemId) {
    return null
  }

  if (state.project.partTypes?.[bomItemId]) {
    return BomItemType.partType
  }

  if (state.project.partInstances?.[bomItemId]) {
    return BomItemType.partInstance
  }

  if (state.project.assemblyHeaders?.[bomItemId]) {
    return BomItemType.assemblyType
  }

  if (state.project.assemblyInstances?.[bomItemId]) {
    return BomItemType.assemblyInstance
  }

  return null
}

const QuotationResponseItemRow = ({
  rfqItem,
  responsePrice,
  onResponsePriceChange,
}: {
  rfqItem: RfqBomItem
  responsePrice: MoneyDto | null
  onResponsePriceChange: (bomItemId: string, price: MoneyDto | null) => void
}) => {
  const getLocalizedWorkingStepLabel = useLocalizedWorkingStepLabel()

  const bomItemType = useAppSelector((state: RootAppState) =>
    getBomItemType(state, rfqItem.bomItemId),
  )

  const bomItemPointer = useMemo<BomItemPointer | null>(() => {
    if (!bomItemType) {
      return null
    }

    return {
      id: rfqItem.bomItemId,
      type: bomItemType,
    }
  }, [bomItemType, rfqItem.bomItemId])

  const bomItem = useAppSelector(
    useCallback(
      (state: RootAppState) => {
        if (!bomItemPointer) {
          return null
        }

        return bomItemSelector(state, bomItemPointer)
      },
      [bomItemPointer],
    ),
    isEqual,
  ) as AssemblyHeaderRow | PartTypeRow | null

  const currentPrice = useAppSelector(
    useCallback(
      (state: RootAppState) => {
        if (!bomItemPointer) {
          return null
        }

        return projectSelectors.bomItemFinancialSelector(
          bomItemPointer,
          'salesPricePerItem',
        )(state)
      },
      [bomItemPointer],
    ),
    isEqual,
  ) as MoneyDto | null

  const bomItemQuantity = useAppSelector(
    useCallback(
      (state: RootAppState) => {
        if (!bomItemPointer) {
          return null
        }

        return bomItemQuantitySelector(bomItemPointer)(state)
      },
      [bomItemPointer],
    ),
    isEqual,
  )

  const thumbnailSrc = useBomItemThumbnailImageSrc(
    bomItemPointer || { id: '', type: BomItemType.partType },
  )

  const itemName =
    bomItem?.name || bomItem?.originalFileName || rfqItem.bomItemId

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {thumbnailSrc.imageSrc ? (
            <Box
              component="img"
              src={thumbnailSrc.imageSrc}
              alt={itemName}
              sx={{
                width: 36,
                height: 36,
                objectFit: 'contain',
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
                p: 0.25,
                flexShrink: 0,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              N/A
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {itemName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {rfqItem.bomItemId}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(rfqItem.activities ?? []).length > 0 ? (
            (rfqItem.activities ?? []).map((activity, index) => (
              <Chip
                key={`${rfqItem.bomItemId}-${activity.workingStepType}-${index}`}
                label={
                  activity.workingStepType
                    ? getLocalizedWorkingStepLabel(
                        activity.workingStepType as WorkingStepType,
                      )
                    : 'Activity'
                }
                size="small"
                variant="outlined"
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{bomItemQuantity ?? '-'}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{formatMoney(currentPrice)}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ minWidth: 180 }}>
        {bomItemPointer ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <MoneyInput
              defaultMoneyObject={responsePrice}
              onChange={(event) => {
                const moneyDto = event.currentTarget.value

                onResponsePriceChange(rfqItem.bomItemId, moneyDto)
              }}
              value={responsePrice}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Item not found in project
          </Typography>
        )}
      </TableCell>
    </TableRow>
  )
}

export const ManageQuotationResponseDialog = ({
  isOpen,
  onClose,
}: ManageQuotationResponseDialogProps) => {
  const projectId = useAppSelector(projectIdSelector)
  const rfqMetadata = useAppSelector(
    (state) => state.project.activeProject?.rfqMetadata,
  )

  const { controller: rfqController, loading: rfqLoading } = useAppController(
    () => new RequestForQuotationController(),
  )
  const { controller: quotationController, loading: quotationLoading } =
    useAppController(() => new ProjectQuotationsController())

  const ShowException = useShowException('quotation response')
  const [rfq, setRfq] = useState<RequestForQuotationDto | null>(null)
  const [localResponsePrices, setLocalResponsePrices] =
    useState<LocalResponsePriceMap>({})

  useEffect(() => {
    if (!isOpen || !rfqMetadata?.createdFromRequestForQuotationId) {
      return undefined
    }

    let isMounted = true

    const fetchRfq = async () => {
      try {
        const response = await rfqController.GetRfqById(
          rfqMetadata.createdFromRequestForQuotationId,
        )

        if (isMounted) {
          setRfq(response)
        }
      } catch (error) {
        ShowException(error)
      }
    }

    fetchRfq()

    return () => {
      isMounted = false
    }
  }, [
    isOpen,
    rfqController,
    rfqMetadata?.createdFromRequestForQuotationId,
    ShowException,
  ])

  const sendDisabled =
    !projectId ||
    !rfqMetadata?.forwardId ||
    !rfqMetadata?.forwardSender ||
    quotationLoading[`PostQuotationResponseToSCSN-${projectId}`]

  const rfqItems = useMemo(() => rfq?.bomItemsAndActivities ?? [], [rfq])

  const rfqItemCurrentPrices = useAppSelector(
    useCallback(
      (state: RootAppState) => {
        const pricesByBomItemId: LocalResponsePriceMap = {}

        for (const item of rfqItems) {
          const bomItemType = getBomItemType(state, item.bomItemId)
          if (!bomItemType) {
            continue
          }

          const bomItemPointer: BomItemPointer = {
            id: item.bomItemId,
            type: bomItemType,
          }

          const currentPrice = projectSelectors.bomItemFinancialSelector(
            bomItemPointer,
            'salesPricePerItem',
          )(state)

          if (currentPrice) {
            pricesByBomItemId[item.bomItemId] = currentPrice
          }
        }

        return pricesByBomItemId
      },
      [rfqItems],
    ),
    isEqual,
  )

  const [quotationResponse, setQuotationResponse] = useState<Quotation | null>(
    null,
  )

  const fetchQuotationResponse = useCallback(async () => {
    try {
      const resp = await quotationController.GetQuotationResponse()
      setQuotationResponse(resp.quotation)
    } catch (err) {
      ShowException(err)
    }
  }, [quotationController, ShowException])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    fetchQuotationResponse()

    setLocalResponsePrices((previous) => {
      const initialPrices: LocalResponsePriceMap = {}

      for (const item of rfqItems) {
        const existing = previous[item.bomItemId]
        if (existing) {
          initialPrices[item.bomItemId] = existing
          continue
        }

        const current = rfqItemCurrentPrices[item.bomItemId]
        if (current) {
          initialPrices[item.bomItemId] = current
        }
      }

      return isEqual(previous, initialPrices) ? previous : initialPrices
    })
  }, [isOpen, rfqItems, rfqItemCurrentPrices, fetchQuotationResponse])

  const handleLocalPriceChange = useCallback(
    (bomItemId: string, price: MoneyDto | null) => {
      setLocalResponsePrices((previous) => {
        if (!price) {
          const { [bomItemId]: _removed, ...rest } = previous
          return rest
        }

        return {
          ...previous,
          [bomItemId]: price,
        }
      })
    },
    [],
  )

  const handleCreate = async () => {
    if (!projectId || !rfqMetadata?.forwardId || !rfqMetadata?.forwardSender) {
      return
    }

    const quotationResponsePrices: Array<QuotationLineDto> = []

    for (const item of rfqItems) {
      const localPrice = localResponsePrices[item.bomItemId]
      if (!localPrice || localPrice.amount === undefined) {
        continue
      }

      const bomItem = bomItemSelector(store.getState(), {
        id: item.bomItemId,
        type: undefined,
      }) as AssemblyHeaderRow | PartTypeRow

      quotationResponsePrices.push({
        id: bomItem.id,
        comment: bomItem.comment || 'Price provided in quotation response',
        salesPrice: localPrice,
        name: bomItem.name,
        quantity: bomItem.financial.totalProjectQuantity,
      })
    }

    try {
      await quotationController.CreateQuotationResponse({
        forwardId: rfqMetadata.forwardSender,
        forwardSender: rfqMetadata.forwardId,
        lines: quotationResponsePrices,
      })

      onClose()
    } catch (error) {
      ShowException(error)
    }
  }

  const handleSend = async (quotationId: string) => {
    try {
      await quotationController.SendQuotationResponseToBuyer(quotationId)
    } catch (err) {
      ShowException(err)
    }
  }

  const handleDelete = async (quotationId: string) => {
    try {
      await quotationController.DeleteQuotationResponse(quotationId)
    } catch (err) {
      ShowException(err)
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          Quotation Response
          <Typography variant="caption" color="textSecondary">
            {quotationResponse?.id || 'quotation id not found'}
          </Typography>
        </div>
        {quotationResponse && (
          <Chip
            label={quotationResponse.state}
            sx={{ alignSelf: 'center' }}
            color={quotationResponseColorMap[quotationResponse.state].color}
            variant={quotationResponseColorMap[quotationResponse.state].variant}
            size="small"
          />
        )}
        <DeleteButtonWithConfirmation
          color="error"
          buttonType="button"
          onDelete={() => handleDelete(quotationResponse.id)}
          rootItemProps={{
            style: { marginLeft: 'auto' },
          }}
          muiButtonProps={{
            size: 'medium',
            disabled: sendDisabled,
            startIcon: quotationLoading[
              `DeleteQuotationResponse-${projectId}`
            ] ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined,
          }}
        />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Review the RFQ items, adjust the response price when needed, and
            send the quotation response back to the buyer.
          </Typography>

          <Alert severity="info">
            Response prices in this dialog are local. They are sent with the
            quotation response and do not update BOM financials.
          </Alert>

          {!rfqMetadata?.forwardId || !rfqMetadata?.forwardSender ? (
            <Alert severity="warning">
              Missing RFQ routing metadata. The quotation response cannot be
              sent from this project.
            </Alert>
          ) : null}

          {rfqLoading[
            `GetRfqById-${rfqMetadata?.createdFromRequestForQuotationId}`
          ] ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rfqItems.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Activities</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Current price</TableCell>
                  <TableCell align="right">Response price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rfqItems.map((item, index) => (
                  <QuotationResponseItemRow
                    key={`${item.bomItemId}-${index}`}
                    rfqItem={item}
                    responsePrice={localResponsePrices[item.bomItemId] || null}
                    onResponsePriceChange={handleLocalPriceChange}
                  />
                ))}
              </TableBody>
            </Table>
          ) : (
            <Alert severity="warning">
              No RFQ items were found for this project. The quotation response
              can still be sent, but there are no line prices to review here.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!quotationResponse && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={sendDisabled}
            startIcon={
              quotationLoading[`PostQuotationResponseToSCSN-${projectId}`] ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Create quotation response
          </Button>
        )}
        {quotationResponse && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="contained"
              onClick={() => handleSend(quotationResponse.id)}
              disabled={sendDisabled}
              startIcon={
                quotationLoading[
                  `SendQuotationResponseToBuyer-${projectId}`
                ] ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Send quotation response to buyer
            </Button>
          </div>
        )}
      </DialogActions>
    </Dialog>
  )
}
